import React, { useEffect, useRef, useState } from "react";
import Checkbox from "../../components/Checkbox";
import Table, { Column, TableHandle } from "../../components/Table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { Button } from "../../components/Button";
import { FaEdit } from "react-icons/fa";
import { LightColors } from "../../utils/colors/colors";
import { setStreetFilterData } from "../../redux/slices/StreetManagement/slice";
import { fetchStreetList } from "../../redux/slices/StreetManagement/thunk";
import FilterForm from "./components/FilterForm";
import AddStreet from "./components/AddStreet";
import { StreetType } from "../../redux/slices/StreetManagement/slice";

const StreetManagement = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tableRef = useRef<TableHandle>(null);

  const {
    loading,
    filterData,
    paginationData,
    streetList
  } = useSelector((state: RootState) => state.street);

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStreet, setSelectedStreet] = useState<StreetType | null>(null);
  const [refresh, setRefresh] = useState(false)


  /* =========================
     TABLE COLUMNS
  ========================= */

  const columns: Column<StreetType>[] = [
    {
      key: "street",
      label: "Street Name",
      sortable: true,
    },
    {
      key: "street", // ✅ valid key
      label: "Area",
      sortable: false,
      customCell: (row) => row.area?.area || "-",
    },
    {
      key: "street", // ✅ valid key
      label: "City",
      sortable: false,
      customCell: (row) => row.area?.city || "-",
    },
    {
      key: "street", // ✅ valid key
      label: "State",
      sortable: false,
      customCell: (row) => row.area?.state || "-",
    },
    {
      key: "isActive",
      label: "Active",
      sortable: false,
      customCell: (row) => (
        <Checkbox checked={row.isActive} onCheckedChange={() => { }} />
      ),
    },
    {
      key: "action",
      label: "Actions",
      sortable: false,
      customCell: (row) => (
        <FaEdit
          size={20}
          className="cursor-pointer"
          color={LightColors.accent}
          onClick={() => {
            setSelectedStreet(row);
            setShowAddModal(true);
          }}
        />
      ),
    },
  ];

  /* =========================
     FETCH DATA
  ========================= */

  useEffect(() => {
    dispatch(fetchStreetList({ ...filterData }));
  }, [
    filterData.page,
    filterData.limit,
    filterData.search,
    filterData.state,
    filterData.city,
    filterData.area,
    filterData.isActive,
    filterData.sortBy,
    filterData.sortOrder,
    refresh
  ]);

  /* =========================
     RENDER
  ========================= */

  return (
    <div>
      <Table
        ref={tableRef}
        columns={columns as any}
        data={streetList?.length ? streetList : []}
        tableName="Street Management"
        totalCount={filterData.totalCount}
        currentPage={filterData.page}
        loading={loading.streetList}
        pageSize={filterData.limit}
        filterContent={<FilterForm tableRef={tableRef} />}
        onSearchChange={(searchText) => {
          dispatch(
            setStreetFilterData({
              ...filterData,
              search: searchText,
              page: 1,
            })
          );
        }}
        onPageChange={(page) => {
          dispatch(
            setStreetFilterData({
              ...filterData,
              page,
            })
          );
        }}
        onPageSizeChange={(size: number) => {
          dispatch(
            setStreetFilterData({
              ...filterData,
              limit: size,
              page: 1,
            })
          );
        }}
        renderRightToolItems={
          <div className='flex gap-2'>

            <Button
              variant="primary"
              onClick={() => setRefresh(!refresh)}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setSelectedStreet(null);
                setShowAddModal(true);
              }}
            >
              Add Street
            </Button>
          </div>
        }
      />

      {/* =========================
          ADD / EDIT MODAL
      ========================= */}

      <AddStreet
        isOpen={showAddModal}
        mode={selectedStreet ? "edit" : "add"}
        initialData={selectedStreet}
        onSubmit={() => {
          setSelectedStreet(null);
          dispatch(fetchStreetList({ ...filterData }));
        }}
        onCancel={() => {
          setShowAddModal(false);
          setSelectedStreet(null);
        }}
        setShowAddModal={setShowAddModal} />
    </div>
  );
};

export default StreetManagement;
